import { publishPrayerCommit } from '@/lib/prayer-authoring';
import { getDb, CONTENT_ENTITIES_STORE, PRAYERS_STORE } from '@/lib/db';
import { completePrayerDocument } from '@/lib/content-lifecycle/contracts';
import type { Prayer } from '@/types/prayer';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';
import {
  authorPrayerAttempt,
  registerPrayerAttempt,
  listPrayerAttempts,
  authorPrayerMutationCas,
  type ContentLifecycleAuthExpectation,
} from '@/lib/content-lifecycle/store';
import {
  getCurrentContentLifecycleContext,
  initContentLifecycleSync,
  isPrayerReadyForContentSync,
  configureContentLifecycleFeatureScope,
  syncContentLifecycle,
  type ContentSyncResult,
} from '@/lib/content-lifecycle/sync';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { useHighlightStore } from '@/lib/highlight-store';
import { useAdiaStore } from '@/lib/store';
import { dispatchSyncSettled } from '@/lib/sync-events';

/**
 * Compatibility facade for prayer callers. Ordinary transport is owned by
 * content-lifecycle/sync.ts; this module no longer performs timestamp pulls,
 * direct table writes, or a per-type single-flight.
 */

export async function syncPrayers(): Promise<ContentSyncResult> {
  if (isCoreReadOnly()) {
    return { pushed: 0, replayed: 0, pulled: 0, conflicts: 0, held: false };
  }
  return syncContentLifecycle();
}

export async function pushPrayer(prayer: Prayer): Promise<boolean> {
  if (!(await queuePrayerMutation(prayer))) return false;
  await syncContentLifecycle();
  return true;
}

/** Persist the authoritative tuple + visible projection atomically, without waiting for network. */
export async function queuePrayerMutation(
  prayer: Prayer,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
  expectedAuth?: ContentLifecycleAuthExpectation,
): Promise<boolean> {
  if (isCoreReadOnly() || !isPrayerReadyForContentSync(prayer)) return false;
  const context = await getCurrentContentLifecycleContext(expectedAuth);
  const db = await getDb();
  const existing = await db.get(CONTENT_ENTITIES_STORE, [context.ownerId, 'prayer', prayer.id]);
  const projection = await db.get(PRAYERS_STORE, prayer.id);
  if (existing?.visibleDocument && projection && JSON.stringify(existing.visibleDocument) === JSON.stringify(completePrayerDocument(prayer))) {
    if (recoveredAudio.length) {
      for (const claim of recoveredAudio) {
        const row = await db.get('audio-queue', claim.queueItemId);
        if (!row || row.ownerId !== context.ownerId || row.destinationEntityId !== prayer.id || !row.destinationOpId || row.localDisposition !== 'destination-commit-pending') throw new Error('prayer-create-audio-retry-unproven');
      }
    }
    return true;
  }
  const retained = (await listPrayerAttempts(context.ownerId)).find(item => item.prayerId === prayer.id && item.intent.kind === 'create');
  if (retained && JSON.stringify(retained.intent) !== JSON.stringify({ kind: 'create', prayer })) throw new Error('prayer-create-attempt-differs');
  const attempt = retained ?? await registerPrayerAttempt(context, prayer.id, { kind: 'create', prayer }, recoveredAudio);
  const result = await authorPrayerAttempt(context, attempt);
  if (result.kind !== 'committed') return false;
  publishPrayerCommit(result);
  return true;
}

export async function queuePrayerMutationCas(
  prayerId: string,
  guard: (current: Prayer) => boolean,
  computeNext: (current: Prayer) => Prayer,
): Promise<Prayer | null> {
  if (isCoreReadOnly()) return null;
  const context = await getCurrentContentLifecycleContext();
  return authorPrayerMutationCas(context, prayerId, guard, computeNext);
}

export function requestContentLifecycleSync(): void {
  void syncContentLifecycle().catch(() => {
    // The durable tuple is already committed; reconnect retries it.
  });
}

let initialized = false;

export function initOutbox(featureScope: {
  bookmarks: boolean;
  highlights: boolean;
}): void {
  if (isCoreReadOnly() || initialized) return;
  initialized = true;
  configureContentLifecycleFeatureScope(featureScope);
  initContentLifecycleSync(async () => {
    const loads: Promise<unknown>[] = [useAdiaStore.getState().loadPrayers()];
    if (featureScope.bookmarks) {
      loads.push(useBookmarkStore.getState().loadBookmarks());
    }
    if (featureScope.highlights) {
      loads.push(useHighlightStore.getState().loadHighlights());
    }
    await Promise.all(loads);
    dispatchSyncSettled();
  });
}
